import "./styles.scss";
import { SetStateAction, useEffect, useState } from "react";
import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import { useIntl } from "react-intl";

interface IIVehicleAlertsProps {
  alerts: any[];
  closeSidebar: () => void;
}

const VehicleAlerts = ({ alerts, closeSidebar }: IIVehicleAlertsProps) => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState("tab1");
  const [expandedView, setExpandedView] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);

  const handleTabClick = (tab: SetStateAction<string>) => {
    setActiveTab(tab);
    setExpandedView(false);
  };

  const handleExpandClick = (alert: any) => {
    setSelectedAlert(alert);
    setExpandedView(true);
  };

  const handleBackClick = () => {
    setExpandedView(false);
    setSelectedAlert(null);
  };

  const getAlertsByCategory = (category: string) => {
    if (!alerts || !alerts.length) return [];
    return alerts.filter(
      (alert: any) => alert.category === category
    );
  };

  const getUncategorizedAlerts = () => {
    if (!alerts || !alerts.length) return [];
    return alerts.filter(
      (alert: any) =>
        !alert.category ||
        (alert.category !== "brake" &&
          alert.category !== "fluid" &&
          alert.category !== "battery" &&
          alert.category !== "components")
    );
  };

  const hasAlerts = (category: string) => {
    return getAlertsByCategory(category).length > 0;
  };

  const activeAlerts = (() => {
    switch (activeTab) {
      case "tab2":
        return getAlertsByCategory("brake");
      case "tab3":
        return getAlertsByCategory("fluid");
      case "tab4":
        return getAlertsByCategory("battery");
      case "tab1":
        return getAlertsByCategory("components");
      case "tab5":
        return getUncategorizedAlerts();
      default:
        return [];
    }
  })();

  useEffect(() => {
    if (!alerts || !alerts.length) return;

    if (alerts[0]?.category === "brake") {
      setActiveTab("tab2");
    } else if (alerts[0]?.category === "fluid") {
      setActiveTab("tab3");
    } else if (alerts[0]?.category === "battery") {
      setActiveTab("tab4");
    } else if (alerts[0]?.category === "components") {
      setActiveTab("tab1");
    } else {
      setActiveTab("tab5");
    }
  }, [alerts]);

  return (
    <>
      <div className="bg-right-sidebar" onClick={() => closeSidebar()} />
      <div className={`right-sidebar ${isMobile() ? "!w-[80%]" : ""}`}>
        <h3 className="flex gap-6 margin-bottom-20">
          <span className="rioglyph rioglyph-warning-sign"></span>
          {intl.formatMessage({ id: "intl-msg:alerts.title" })}
        </h3>
        <div
          className="close-sidebar cursor-pointer"
          onClick={() => closeSidebar()}
        >
          <span className="rioglyph rioglyph-remove"></span>
        </div>

        <div className="w-full">
          {!expandedView && (
            <div className="flex justify-center alert-tabs">
              <button
                className={`p-4 w-1/4 ${
                  activeTab === "tab1" ? "active-tab" : "text-gray-500"
                }`}
                onClick={() => handleTabClick("tab1")}
              >
                <i className="rioglyph rioglyph-component-custom-recurrent"></i>
                {hasAlerts("components") && (
                  <span className="tab-alert-warning"></span>
                )}
              </button>
              <button
                className={`p-4 w-1/4 ${
                  activeTab === "tab2" ? "active-tab" : "text-gray-500"
                }`}
                onClick={() => handleTabClick("tab2")}
              >
                <i className="rioglyph rioglyph-brake"></i>
                {hasAlerts("brake") && (
                  <span className="tab-alert-warning"></span>
                )}
              </button>
              <button
                className={`p-4 w-1/4 ${
                  activeTab === "tab3" ? "active-tab" : "text-gray-500"
                }`}
                onClick={() => handleTabClick("tab3")}
              >
                <i className="rioglyph rioglyph-temperature"></i>
                {hasAlerts("fluid") && (
                  <span className="tab-alert-warning"></span>
                )}
              </button>
              <button
                className={`p-4 w-1/4 ${
                  activeTab === "tab4" ? "active-tab" : "text-gray-500"
                }`}
                onClick={() => handleTabClick("tab4")}
              >
                <i className="rioglyph rioglyph-battery-level-full"></i>
                {hasAlerts("battery") && (
                  <span className="tab-alert-warning"></span>
                )}
              </button>
              <button
                className={`p-4 w-1/4 ${
                  activeTab === "tab5" ? "active-tab" : "text-gray-500"
                }`}
                onClick={() => handleTabClick("tab5")}
              >
                <i className="rioglyph rioglyph-exclamation"></i>
                {getUncategorizedAlerts().length > 0 && (
                  <span className="tab-alert-warning"></span>
                )}
              </button>
            </div>
          )}

          <div className="alertsTabs">
            {!expandedView ? (
              <div className="alert-tabs__content p-4">
                <h2 className="text-lg font-semibold mb-2">
                  {activeTab === "tab1"
                    ? intl.formatMessage({ id: "intl-msg:alerts.components" })
                    : activeTab === "tab2"
                    ? intl.formatMessage({ id: "intl-msg:alerts.brakes" })
                    : activeTab === "tab3"
                    ? intl.formatMessage({ id: "intl-msg:alerts.fluid" })
                    : activeTab === "tab4"
                    ? intl.formatMessage({ id: "intl-msg:alerts.battery" })
                    : intl.formatMessage({ id: "intl-msg:alerts.others" })}
                </h2>
                {activeAlerts.length > 0 ? (
                  activeAlerts.map((alert: any, index: number) => (
                    <div
                      key={index}
                      className="callout callout-secondary flex items-center gap-6 padding-left-15 mb-4 cursor-pointer"
                      onClick={() => handleExpandClick(alert)}
                    >
                      <span
                        className="rioglyph rioglyph-warning-sign text-color-secondary text-size-20"
                        aria-hidden="true"
                      ></span>
                      <div>
                        <div className="text-color-black text-medium text-size-14 line-height-125rel margin-bottom-0">
                          {alert.spnDescription || alert.description || alert.sourceAddress || intl.formatMessage({ id: "intl-msg:alerts.noDescription" })}
                        </div>
                        <p className="margin-bottom-0 margin-top-0 text-size-12 text-color-gray">
                          SA: {alert.sourceAddressDescription || alert.sourceAddress || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })} | SPN: {alert.spn || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })} | FMI: {alert.fmi || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })}
                        </p>
                        <p className="margin-bottom-0 margin-top-0 text-size-12 text-color-gray">
                          {alert.fmiDescription || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })}
                        </p>
                        <p className="margin-bottom-0 margin-top-0 text-size-11 text-color-gray">
                          {alert.date || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>{intl.formatMessage({ id: "intl-msg:alerts.noAlerts" })}</p>
                )}
              </div>
            ) : (
              <div className="expanded-view">
                <div className="flex items-center gap-4 expanded-view__header">
                  <button onClick={handleBackClick} className="m-0 back-button">
                    <span className="rioglyph rioglyph-arrow-left"></span>
                  </button>
                  <p className="text-lg font-semibold m-0">{intl.formatMessage({ id: "intl-msg:alerts.details" })}</p>
                </div>
                {selectedAlert && (
                  <div className="flex flex-col alert-detail">
                    <div className="flex justify-between alert-item">
                      <span>SA:</span>
                      <span>
                        {selectedAlert?.sourceAddressDescription || selectedAlert?.sourceAddress || intl.formatMessage({ id: "intl-msg:alerts.notInformed" })}
                      </span>
                    </div>
                    <div className="flex justify-between alert-item">
                      <span>{intl.formatMessage({ id: "intl-msg:alerts.spnDescription" })}</span>
                      <span>{selectedAlert?.spnDescription || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })}</span>
                    </div>
                    <div className="flex justify-between alert-item">
                      <span>{intl.formatMessage({ id: "intl-msg:alerts.fmiDescription" })}</span>
                      <span>{selectedAlert?.fmiDescription || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })}</span>
                    </div>
                    <div className="flex justify-between alert-item">
                      <span>{intl.formatMessage({ id: "intl-msg:alerts.fmiCode" })}</span>
                      <span>{selectedAlert?.fmi || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })}</span>
                    </div>
                    <div className="flex justify-between alert-item">
                      <span>{intl.formatMessage({ id: "intl-msg:alerts.spnCode" })}</span>
                      <span>{selectedAlert?.spn || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })}</span>
                    </div>
                    <div className="flex justify-between alert-item">
                      <span>{intl.formatMessage({ id: "intl-msg:alerts.dateTime" })}</span>
                      <span>{selectedAlert?.date || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })}</span>
                    </div>
                    <div className="flex justify-between alert-item">
                      <span>{intl.formatMessage({ id: "intl-msg:alerts.description" })}</span>
                      <span>{selectedAlert?.description || intl.formatMessage({ id: "intl-msg:alerts.notAvailable" })}</span>
                    </div>
                    <div className="flex justify-between alert-item">
                      <span>{intl.formatMessage({ id: "intl-msg:alerts.status" })}</span>
                      <span>{selectedAlert?.lampStatus || selectedAlert?.status || intl.formatMessage({ id: "intl-msg:alerts.notInformed" })}</span>
                    </div>
                    {selectedAlert?.km && (
                      <div className="flex justify-between alert-item">
                        <span>{intl.formatMessage({ id: "intl-msg:alerts.odometer" })}</span>
                        <span>{selectedAlert.km} km</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VehicleAlerts;
